import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faLinkedin, 
  faFacebook 
} from "@fortawesome/free-brands-svg-icons";
import {
  faEnvelope,
  faStethoscope,
  faGraduationCap,
  faChevronLeft,
  faChevronRight,
  faPlay,
  faPause,
} from "@fortawesome/free-solid-svg-icons";
import { useContent, useTeamMembers } from "../hooks/useContent";
import { imageUrls } from "../lib/supabase";

const TeamMember = ({
  imageSrc,
  name,
  title,
  qualifications,
  socialLinks,
  specialization,
}) => (
  <div className="team-card">
    <div className="team-img-container">
      <img src={imageSrc} alt={name} className="team-img" />
      <div className="team-social">
        {socialLinks &&
          socialLinks.map((link, index) => (
            <a
              key={index}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={link.label}
            >
              <FontAwesomeIcon icon={link.icon} />
            </a>
          ))}
      </div>
    </div>
    <div className="team-info">
      <h3>{name}</h3>
      <p className="team-title">{title}</p>
      <div className="team-qualifications">
        {qualifications &&
          qualifications.map((qualification, index) => (
            <div key={index} className="qualification-item">
              <FontAwesomeIcon
                icon={index === 0 ? faStethoscope : faGraduationCap}
                className="qualification-icon"
              />
              <span>{qualification}</span>
            </div>
          ))}
      </div>
      {specialization && (
        <div className="team-specialization">
          <p>{specialization.description}</p>
          {specialization.link && (
            <a
              href={specialization.link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="specialization-link"
            >
              {specialization.link.text}
            </a>
          )}
        </div>
      )}
    </div>
  </div>
);

const OurTeam = () => {
  // Dynamic content from Supabase
  const { content: sectionTitle } = useContent(
    "team_section_title",
    "home",
    "Echipa noastră"
  );
  const { teamMembers, loading } = useTeamMembers();

  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  // Fallback team data with Supabase URLs
  const fallbackTeamMembers = [
    {
      id: 1,
      name: "Dr. Ali Cranta",
      specialty: "Medic primar pneumolog",
      description: "Specialist în afecțiuni pulmonare cu peste 20 de ani de experiență.",
      image_url: imageUrls.drAli(),
    },
    {
      id: 2,
      name: "Dr. Diana Drugă",
      specialty: "Medic primar pneumolog",
      description: "Expert în diagnosticul și tratamentul bolilor respiratorii.",
      image_url: imageUrls.drDruga(),
    },
    {
      id: 3,
      name: "Dr. Adriana Popescu",
      specialty: "Psiholog clinician",
      description: "Specialist în evaluări psihologice și consiliere comportamentală.",
      image_url: imageUrls.drPopescu(),
    },
  ];

  // Use team members from Supabase if available, otherwise use fallback
  const displayTeamMembers =
    teamMembers.length > 0 ? teamMembers : fallbackTeamMembers;

  // Carousel configuration
  const itemsPerSlide = 3;
  const totalSlides = Math.ceil(displayTeamMembers.length / itemsPerSlide);

  // Helper function to generate default qualifications if missing
  const generateDefaultQualifications = (member) => {
    if (member.qualifications && member.qualifications.length > 0) {
      return member.qualifications;
    }
    
    // Generate default qualifications based on title and specialization
    const qualifications = [];
    
    if (member.title) {
      qualifications.push(member.title);
    }
    
    if (member.specialization && member.specialization !== member.title) {
      // Handle both string and object specialization formats
      const specializationText = typeof member.specialization === 'object' 
        ? member.specialization.description 
        : member.specialization;
      
      if (specializationText && specializationText !== member.title) {
        qualifications.push(specializationText);
      }
    }
    
    return qualifications;
  };

  // Helper function to convert specialization to object format if it's a string
  const normalizeSpecialization = (specialization) => {
    if (!specialization) return null;
    
    if (typeof specialization === 'object') {
      return specialization;
    }
    
    // Convert string to object format
    return {
      description: specialization
    };
  };

  // Helper function to convert social_links object to array format for TeamMember component
  const convertSocialLinks = (socialLinks) => {
    if (!socialLinks) return [];

    const links = [];
    if (socialLinks.email) {
      links.push({
        icon: faEnvelope,
        url: `mailto:${socialLinks.email}`,
        label: "Email",
      });
    }
    if (socialLinks.facebook) {
      links.push({
        icon: faFacebook,
        url: socialLinks.facebook,
        label: "Facebook",
      });
    }
    if (socialLinks.linkedin) {
      links.push({
        icon: faLinkedin,
        url: socialLinks.linkedin,
        label: "LinkedIn",
      });
    }
    return links;
  };

  // Navigation functions
  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  const goToSlide = useCallback((slideIndex) => {
    setCurrentSlide(slideIndex);
  }, []);

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  // Auto-scroll functionality
  useEffect(() => {
    if (!isAutoPlaying || isPaused || totalSlides <= 1) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, isPaused, nextSlide, totalSlides]);

  // Get current slide items
  const getCurrentSlideItems = () => {
    const startIndex = currentSlide * itemsPerSlide;
    return displayTeamMembers.slice(startIndex, startIndex + itemsPerSlide);
  };

  if (loading) {
    return (
      <section className="section team-section" id="our-team">
        <div className="container">
          <div className="team-loading">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Se încarcă echipa...</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="section team-section"
      id="our-team"
    >
      <div className="container">
        <h2 className="section-title">{sectionTitle}</h2>

        {/* Carousel Container */}
        <div 
          className="team-carousel-container"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Carousel Header with Controls */}
          <div className="carousel-header">
            <div className="carousel-navigation">
              <button
                className="carousel-nav-btn carousel-prev"
                onClick={prevSlide}
                disabled={totalSlides <= 1}
                aria-label="Echipa anterioară"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>

              <div className="slide-indicator">
                {currentSlide + 1} / {totalSlides}
              </div>

              <button
                className="carousel-nav-btn carousel-next"
                onClick={nextSlide}
                disabled={totalSlides <= 1}
                aria-label="Echipa următoare"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>

              {totalSlides > 1 && (
                <button
                  className="carousel-autoplay-btn"
                  onClick={toggleAutoPlay}
                  aria-label={isAutoPlaying ? "Oprește redarea automată" : "Pornește redarea automată"}
                >
                  <FontAwesomeIcon icon={isAutoPlaying ? faPause : faPlay} />
                </button>
              )}
            </div>
          </div>

          {/* Carousel Slides */}
          <div className="carousel-slides">
            <div className="carousel-track">
              <div className="row g-4 carousel-slide active">
                {getCurrentSlideItems().map((member) => (
                  <div className="col-lg-4 col-md-6" key={member.id}>
                    <TeamMember
                      imageSrc={member.image_url}
                      name={member.name}
                      title={member.title}
                      qualifications={generateDefaultQualifications(member)}
                      socialLinks={convertSocialLinks(member.social_links)}
                      specialization={normalizeSpecialization(member.specialization)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Carousel Dots */}
          {totalSlides > 1 && (
            <div className="carousel-dots">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  className={`dot ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Mergi la grupa ${index + 1} din echipă`}
                />
              ))}
            </div>
          )}

          {/* Progress bar for auto-scroll */}
          {isAutoPlaying && !isPaused && totalSlides > 1 && (
            <div className="carousel-progress">
              <div className="progress-bar" />
            </div>
          )}
        </div>

        {/* Team Statistics or Additional Info */}
        <div className="team-stats">
          <div className="stat-item">
            <span className="stat-number">{displayTeamMembers.length}</span>
            <span className="stat-label">Membri în echipă</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">20+</span>
            <span className="stat-label">Ani de experiență</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">100%</span>
            <span className="stat-label">Dedicare pentru pacienți</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurTeam;
